import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSd, errText } from "@/components/servicedesk/SdBits";

const Chips = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {options.map((o) => { const on = value.includes(o.value); return (
      <button key={o.value} type="button" onClick={() => onChange(on ? value.filter((x) => x !== o.value) : [...value, o.value])}
        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>{o.label}</button>); })}
  </div>
);

const TeamForm = ({ init, staff, companies, cat, onDone, onCancel }) => {
  const [f, setF] = useState(init);
  const pool = staff.filter((s) => s.active && s.role !== "Auditor");
  const save = async () => {
    try {
      const body = { name: f.name, support_group: f.support_group, company_ids: f.company_ids, leader_id: f.leader_id || "", member_ids: f.member_ids, active: f.active };
      if (f.id) await api.put(`/servicedesk/teams/${f.id}`, body); else await api.post("/servicedesk/teams", body);
      toast.success("Team saved"); onDone();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="sd-team-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><div className="text-xs text-slate-500 mb-1">Team name</div><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Hardware Team" data-testid="sd-team-name" /></div>
        <div><div className="text-xs text-slate-500 mb-1">Support group</div>
          <Select value={f.support_group} onValueChange={(v) => setF({ ...f, support_group: v })}><SelectTrigger data-testid="sd-team-group"><SelectValue /></SelectTrigger>
            <SelectContent>{cat.groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><div className="text-xs text-slate-500 mb-1">Team Leader</div>
        <Select value={f.leader_id || "none"} onValueChange={(v) => setF({ ...f, leader_id: v === "none" ? "" : v })}><SelectTrigger className="sm:w-80" data-testid="sd-team-leader"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">No leader yet</SelectItem>{pool.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name} · {s.role}</SelectItem>)}</SelectContent></Select></div>
      <div><div className="text-xs text-slate-500 mb-1">Members</div><Chips options={pool.filter((s) => s.user_id !== f.leader_id).map((s) => ({ value: s.user_id, label: s.name }))} value={f.member_ids} onChange={(v) => setF({ ...f, member_ids: v })} testid="sd-team-members" /></div>
      <div><div className="text-xs text-slate-500 mb-1">Companies supported (none = all)</div><Chips options={companies.map((c) => ({ value: c.id, label: c.name }))} value={f.company_ids} onChange={(v) => setF({ ...f, company_ids: v })} testid="sd-team-companies" /></div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="sd-team-active" />Active</label>
      <div className="flex gap-2"><Button onClick={save} disabled={!f.name.trim()} data-testid="sd-team-save">Save team</Button><Button variant="outline" onClick={onCancel}>Cancel</Button></div>
    </div>
  );
};

const TeamCard = ({ t, companies, onEdit }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-4 ${t.active ? "" : "opacity-60"}`} data-testid={`sd-team-${t.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div><div className="font-semibold text-slate-900">{t.name}</div><div className="text-xs text-slate-500">{t.support_group}{t.active ? "" : " · Inactive"}</div></div>
      <div className="text-right"><div className="text-xl font-semibold tabular-nums">{t.open_tickets}</div><div className="text-[11px] text-slate-400">open tickets</div></div>
    </div>
    <div className="mt-3 text-xs space-y-1">
      <div className="flex items-center gap-1.5"><Icons.Crown className="h-3.5 w-3.5 text-amber-500" />{t.leader_name || <span className="text-amber-600">No Team Leader</span>}</div>
      <div className="flex items-start gap-1.5"><Icons.Users className="h-3.5 w-3.5 text-slate-400 mt-0.5" />{t.members?.length ? t.members.map((m) => m.name).join(", ") : "No members"}</div>
      <div className="flex items-start gap-1.5"><Icons.Building2 className="h-3.5 w-3.5 text-slate-400 mt-0.5" />{t.company_ids?.length ? t.company_ids.map((id) => companies.find((c) => c.id === id)?.name || id).join(", ") : "All companies"}</div>
    </div>
    {onEdit && <Button size="sm" variant="outline" className="mt-3" onClick={() => onEdit(t)} data-testid={`sd-team-edit-${t.id}`}>Edit</Button>}
  </div>
);

export default function SdTeams() {
  const { me, cat } = useSd();
  const [teams, setTeams] = useState([]);
  const [staff, setStaff] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/servicedesk/teams").then(({ data }) => setTeams(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/servicedesk/staff").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/companies").then(({ data }) => setCompanies(Array.isArray(data) ? data : data.rows || [])).catch(() => {});
  }, []);
  if (me && !me.is_staff) return <Navigate to="/servicedesk/my" replace />;
  if (!me || !cat) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  const admin = me.can_settings;
  return (
    <div data-testid="sd-teams-page">
      <PageHeader title="IT Teams" subtitle="Teams per support group, each with a Team Leader who manages its queue"
        actions={admin && !form && <Button onClick={() => setForm({ name: "", support_group: cat.groups[0], company_ids: [], leader_id: "", member_ids: [], active: true })} data-testid="sd-team-new"><Icons.Plus className="h-4 w-4 mr-1" />New team</Button>} />
      {admin && !staff.some((s) => s.active) && <p className="text-sm text-amber-700 mb-3">Add IT staff in Service Desk Settings first.</p>}
      {form && <div className="mb-4"><TeamForm init={form} staff={staff} companies={companies} cat={cat} onCancel={() => setForm(null)} onDone={() => { setForm(null); load(); }} /></div>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {teams.map((t) => <TeamCard key={t.id} t={t} companies={companies} onEdit={admin ? (x) => setForm({ ...x, member_ids: x.member_ids || [] }) : null} />)}
        {!teams.length && <div className="text-sm text-slate-500 p-6" data-testid="sd-teams-empty">No teams yet.</div>}
      </div>
    </div>
  );
}
